import { useContext, useState, useEffect, useMemo } from "react";
import { useWeb3React } from "@web3-react/core";
import Image from "next/image";
import {
  InputGroup,
  Input,
  InputLeftElement,
  InputRightElement,
  Box,
  Flex,
  Text,
  Spinner,
  Button as CButton,
  UnorderedList,
  ListItem,
  Center,
} from "@chakra-ui/react";
import { FiPlus, FiChevronRight } from "react-icons/fi";
import { AiOutlineCheckCircle } from "react-icons/ai";

// Contexts
import { StakeContext } from "providers/StakeProvider";

// Components
import Alert from "components/Alert";
import StakingLimit from "components/StakingLimit";
import Button from "./CustomButtom";

// Utils
import {
  isFloat,
  isNumber,
  toFixedWithoutRounding,
  toStringPrecision,
} from "utils/common";

// Constants
import { CLICK_STAKE_INPUT_MAX } from "constants/analytics";
import { parseEther } from "@ethersproject/units";
import { BigNumber } from "ethers";

const Stake = () => {
  const { active } = useWeb3React();
  const {
    balLoading: loading,
    walletInfo,
    openWalletModal,
    addTokenToUserWallet,
    handleApprove,
    handleStake,
    conversionFromRate: conversionRate,
    maxAllowance,
    instantPool,
    isPolygonChainActive,
    stakingLimitDetails,
    isValidChainSelected,
    switchToPolygon,
    track,
  } = useContext(StakeContext);
  const {
    tokenBalance,
    tokenSymbol,
    xTokenSymbol,
    xTokenBalance,
    xtokenIcon,
    icon,
  } = walletInfo;

  const [input, setInput] = useState("");
  const [convertedAmount, setConvertedAmount] = useState("0");
  const [error, setError] = useState("");
  const [isLowGasFeeTxn, setLowGasFeeTxn] = useState(false);

  const inputInWei = useMemo(() => parseEther(input || "0"), [input]);

  const isApproved = useMemo(
    () =>
      input
        ? BigNumber.from(maxAllowance).gt(inputInWei)
        : BigNumber.from(maxAllowance).gt(0),
    [input, inputInWei, maxAllowance]
  );

  useEffect(() => {
    if (input && isNumber(input)) {
      const value = Number(input) * Number(conversionRate);
      const formatteValue = isFloat(value)
        ? toFixedWithoutRounding(toStringPrecision(value), 4)
        : toStringPrecision(value);

      if (!Number.isNaN(+formatteValue)) {
        setConvertedAmount(formatteValue);
      } else {
        setConvertedAmount("0");
      }
    } else {
      setConvertedAmount("0");
    }
  }, [input, conversionRate]);

  useEffect(() => {
    if (input) {
      const error = checkInput(input);
      setError(error);
    }
  }, [tokenBalance]);

  useEffect(() => {
    if (input && (!active || !isApproved)) {
      setInput("");
    }
  }, [active]);

  const checkInput = (value = input) => {
    if (!active) return "";

    let error = "";

    if (!+value || Number.isNaN(+value)) {
      error = "This field can't be blank or zero. Please provide valid input.";
    } else if (Number(value) > Number(tokenBalance)) {
      error = "Insufficient Funds.";
    }

    return error;
  };

  const handleChange = (value: string) => {
    const val = value.trim();

    if (isNumber(val) || val === "") {
      const error = checkInput(val);

      if (+val < instantPool) {
        setLowGasFeeTxn(true);
      } else {
        setLowGasFeeTxn(false);
      }

      if (val.indexOf(".") === -1) {
        setInput(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= 8) {
          setInput(val === "." ? "0." : val);
        }
      }

      setError(error);
    }
  };

  const handleMaxClick = () => {
    track(CLICK_STAKE_INPUT_MAX, {
      stake_amount_entered: (+tokenBalance).toString(),
    });
    handleChange((+tokenBalance).toString());
  };

  const handleClick = () => {
    const error = checkInput();
    if (error) {
      setError(error);
    } else {
      handleStake(toFixedWithoutRounding(toStringPrecision(+input), 8));
      setInput("");
      setLowGasFeeTxn(false);
    }
  };

  const { totalLimit, exhaustedLimit } = stakingLimitDetails;

  const utilised = exhaustedLimit + (checkInput() ? 0 : +input);

  const percentage = +((+utilised / +totalLimit) * 100).toFixed(2);

  return (
    <>
      <Flex alignItems="center" mb="1.5rem" gap="0.5rem">
        <Flex w="100%" gap="1rem" alignItems="center">
          <Flex
            justifyContent="space-between"
            flexGrow={1}
            flexDir={{ base: "column", md: "row" }}
            gap="0.5rem"
          >
            <Box>
              <Text as="span" fontSize="14px">
                My {tokenSymbol} :{" "}
              </Text>
              <Text as="span" fontFamily="semi-bold" color="brand.textColor">
                {tokenBalance}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="14px">
                My {xTokenSymbol} :{" "}
              </Text>
              <Text as="span" fontFamily="semi-bold" color="brand.textColor">
                {xTokenBalance}
              </Text>
            </Box>
          </Flex>
          {active && (
            <Flex
              fontSize="10px"
              bg="brand.primary"
              p="4px 8px"
              color="brand.textLight"
              borderRadius="0.5rem"
              alignItems="center"
              cursor="pointer"
              onClick={addTokenToUserWallet}
            >
              <FiPlus />
              Add {xTokenSymbol}
            </Flex>
          )}
        </Flex>
      </Flex>

      <InputGroup size="lg" mt="0.5rem" mb="0">
        <InputLeftElement pointerEvents="none">
          <Image src={icon} width="28px" height="28px" alt="token-icon" />
        </InputLeftElement>
        <Input
          type="text"
          borderWidth="1px"
          isInvalid={!!error}
          borderColor={error ? "crimson" : "brand.primary"}
          placeholder={
            active && !isApproved && !isPolygonChainActive
              ? "Please approve staking "
              : `Enter ${tokenSymbol} Amount `
          }
          value={input}
          fontSize="1rem"
          onChange={(e) => handleChange(e.target.value)}
          _focus={{
            borderColor: error ? "crimson" : "brand.primary",
            borderWidth: "2px",
          }}
          _hover={{
            borderColor: error ? "crimson" : "brand.primary",
            borderWidth: "2px",
            _disabled: {
              borderWidth: "1px",
            },
          }}
          disabled={active && !isPolygonChainActive && !isApproved}
        />
        <InputRightElement width="4.5rem">
          <CButton
            bg="brand.backgroundLight"
            h="1.75rem"
            size="sm"
            p={1}
            disabled={active && !isPolygonChainActive && !isApproved}
            onClick={
              active && !isPolygonChainActive && !isApproved
                ? undefined
                : handleMaxClick
            }
            _hover={{
              bg: "brand.primary",
              color: "white",
              outline: "none",
              borderColor: "brand.primary",
            }}
            _focus={{
              outline: "none",
            }}
          >
            Max
          </CButton>
        </InputRightElement>
      </InputGroup>

      <Text
        color="brand.errorColor"
        fontSize="14px"
        my="4px"
        visibility={error ? "visible" : "hidden"}
      >
        {error ? error : "error"}
      </Text>

      <Flex
        justifyContent="space-between"
        alignItems={{ base: "flex-start", md: "center" }}
        flexDir={{ base: "column", md: "row" }}
        gap="0.5rem"
      >
        <Flex alignItems="center">
          <Text mr="0.5rem">You will get :</Text>
          <Image
            src={xtokenIcon}
            width="28px"
            height="28px"
            alt="xtoken-icon"
          />
          <Text
            as="span"
            fontSize="20px"
            fontFamily="semi-bold"
            color="brand.textColor"
            ml="0.5rem"
          >
            {convertedAmount}{" "}
            <Text as="span" fontFamily="light" fontSize="1rem">
              {xTokenSymbol}
            </Text>
          </Text>
        </Flex>

        {!isPolygonChainActive && active && isLowGasFeeTxn && !error && (
          <Center color="#45BE21" fontSize="14px" fontFamily="medium">
            <AiOutlineCheckCircle />
            <Text ml="0.5rem">Enabling low GAS mode</Text>
          </Center>
        )}
      </Flex>
      <Alert
        type="info"
        withIcon={false}
        mt="1rem"
        display={isPolygonChainActive ? "none" : "inherit"}
        visibility={isApproved ? "hidden" : "visible"}
      >
        <UnorderedList spacing={3} fontSize="14px" px="0.5rem">
          <ListItem>
            Please{" "}
            <Text as="span" fontFamily="medium">
              Approve
            </Text>{" "}
            to unlock tokens and start staking.
          </ListItem>
        </UnorderedList>
      </Alert>
      {isPolygonChainActive && (
        <StakingLimit utilised={utilised} percentage={percentage} />
      )}
      <Alert
        type="info"
        withIcon={false}
        mt="1rem"
        display={
          active &&
          !error &&
          isPolygonChainActive &&
          totalLimit - exhaustedLimit > 0 &&
          +input > totalLimit - exhaustedLimit
            ? "inherit"
            : "none"
        }
      >
        <UnorderedList spacing={3} fontSize="14px" px="0.5rem">
          <ListItem>
            You have hit the maximum staking limit. Please try with a lesser
            value.
          </ListItem>
        </UnorderedList>
      </Alert>
      <Alert
        type="info"
        withIcon={false}
        mt="1rem"
        display={
          isPolygonChainActive && totalLimit - exhaustedLimit === 0
            ? "inherit"
            : "none"
        }
      >
        <UnorderedList spacing={3} fontSize="14px" px="0.5rem">
          <ListItem>
            <Text>
              Staking limit on Polygon fully utilized. Please check again at 2
              pm UTC when staking limit will be refreshed.
            </Text>
          </ListItem>
        </UnorderedList>
      </Alert>
      <Button
        rightIcon={<FiChevronRight />}
        gap={0}
        onClick={
          error
            ? undefined
            : active
            ? isValidChainSelected
              ? isApproved || isPolygonChainActive
                ? handleClick
                : handleApprove
              : switchToPolygon
            : () => openWalletModal("Stake Tab")
        }
        disabled={
          loading ||
          !!error ||
          (isPolygonChainActive &&
            active &&
            (percentage > 100 || totalLimit - exhaustedLimit === 0))
        }
        mt="1rem"
      >
        {loading ? (
          <Spinner size="md" color="brand.textLight" />
        ) : (
          <Text>
            {active
              ? !isValidChainSelected
                ? "Switch Network"
                : isApproved || isPolygonChainActive
                ? `Stake ${tokenSymbol}`
                : `Approve Staking`
              : "Connect Wallet"}
          </Text>
        )}
      </Button>
    </>
  );
};

export default Stake;
